import {css} from 'styled-components';

import {ActionStyleButtonComponent} from 'components/design-guide/Action/Button';
import {Color} from 'components/design-guide/Action/Button/styles/colors';
import {BUTTON_TYPE_TO_STYLE_MAP} from 'components/design-guide/Action/Button/styles/styleToColorMap';
import {ButtonStateStyles} from 'components/design-guide/Action/Button/styles/types';
import {ActionProps} from 'components/design-guide/Action/types';
import {ButtonStyle, ButtonWidth} from 'components/design-guide/enums';
import {Breakpoints, QUOTING_CARDS_BREAK} from 'styles/Base';

const generateCSSSetForState = (styles?: ButtonStateStyles, override?: boolean) => {
  if (!styles) {
    return css``;
  }
  const optionalImportantRule = override ? ' !important' : '';
  return css`
    color: ${styles.text}${optionalImportantRule};
    background-color: ${styles.fill}${optionalImportantRule};
    border: ${styles.border
        ? `${styles.borderWidth ? styles.borderWidth : '1px'} solid ${styles.border}`
        : `1px solid ${Color.TRANSPARENT}`}${optionalImportantRule};
    box-shadow: ${styles.boxShadow ? styles.boxShadow : ''}${optionalImportantRule};
  `;
};

const getStyleMaps = (buttonStyle: ButtonStyle) => {
  const defaultStyles = BUTTON_TYPE_TO_STYLE_MAP.get(buttonStyle)?.get('default');
  const hoverStyles = BUTTON_TYPE_TO_STYLE_MAP.get(buttonStyle)?.get('hover');
  const disabledStyles = BUTTON_TYPE_TO_STYLE_MAP.get(buttonStyle)?.get('disabled');
  const focusStyles = BUTTON_TYPE_TO_STYLE_MAP.get(buttonStyle)?.get('focus');
  const activeStyles = BUTTON_TYPE_TO_STYLE_MAP.get(buttonStyle)?.get('active');
  const processingStyles = BUTTON_TYPE_TO_STYLE_MAP.get(buttonStyle)?.get('processing');
  const selectedStyles = BUTTON_TYPE_TO_STYLE_MAP.get(buttonStyle)?.get('selected');
  return {defaultStyles, hoverStyles, disabledStyles, focusStyles, activeStyles, processingStyles, selectedStyles};
};

export const generateButtonCSS = (props: ActionProps) => {
  if (props.actionStyle !== 'button' || props.styleProps.buttonStyle === ButtonStyle.TOUCHPOINT) {
    return css``;
  }

  const {defaultStyles, hoverStyles, disabledStyles, focusStyles, activeStyles, processingStyles, selectedStyles} =
    getStyleMaps(props.styleProps.buttonStyle);
  const width = generateButtonWidth(props.styleProps.width);
  const minimumWidth = props.styleProps.buttonStyle === ButtonStyle.SMALL_PILL ? '5rem' : '6.375rem';
  const isDisabled = !!props.disabled;
  const activeSpinner = !!props.styleProps.withSpinner?.active;
  const isSelected = !!props.selected;

  const baseStyles = css`
    display: inline-block;
    min-width: ${minimumWidth};
    ${width}

    &:focus {
      outline: 0;
    }
  `;

  if (activeSpinner) {
    return css`
      ${baseStyles}
      ${ActionStyleButtonComponent} {
        ${generateCSSSetForState(processingStyles)};
        cursor: not-allowed;
      }
    `;
  } else if (isDisabled) {
    return css`
      ${baseStyles}
      ${ActionStyleButtonComponent} {
        ${generateCSSSetForState(disabledStyles)}
        cursor: not-allowed;
      }
    `;
  } else if (isSelected) {
    return css`
      ${baseStyles}
      &:focus > ${ActionStyleButtonComponent} {
        ${generateCSSSetForState(focusStyles)};
      }
      ${ActionStyleButtonComponent} {
        ${generateCSSSetForState(selectedStyles)}
      }
    `;
  }

  return css`
    ${baseStyles}

    ${ActionStyleButtonComponent} {
      ${generateCSSSetForState(defaultStyles)};
    }

    &:focus > ${ActionStyleButtonComponent} {
      ${activeSpinner ? '' : generateCSSSetForState(focusStyles)};
    }

    &:hover > ${ActionStyleButtonComponent} {
      ${activeSpinner ? '' : generateCSSSetForState(hoverStyles)};
    }

    &:active > ${ActionStyleButtonComponent} {
      ${activeSpinner ? '' : generateCSSSetForState(activeStyles)};
    }

    &:disabled > ${ActionStyleButtonComponent} {
      ${activeSpinner ? '' : generateCSSSetForState(disabledStyles)};
      cursor: not-allowed;
    }
  `;
};

const generateButtonWidth = (width?: ButtonWidth | string) => {
  if (width === ButtonWidth.FULL) {
    return css`
      width: 100%;
    `;
  }

  if (width === ButtonWidth.FULL_AT_QUOTE_CARD) {
    return css`
      ${`@media (${QUOTING_CARDS_BREAK})`} {
        width: 100%;
      }
    `;
  }

  if (width === ButtonWidth.FULL_AT_MOBILE) {
    return css`
      ${`@media (${Breakpoints.MOBILE})`} {
        width: 100%;
      }
    `;
  }

  if (width) {
    return css`
      width: ${width};
    `;
  }

  return css`
    width: auto;
  `;
};
